function interpolateValue(lat, lon, points) {
    let weightedSum = 0;
    let weightSum = 0;
    const maxDistance = 0.2; // Radio máximo de búsqueda
    
    // Encontrar los puntos más cercanos
    const nearbyPoints = points.filter(point => {
        const [p_lat, p_lon] = point;
        const dist = Math.sqrt(Math.pow(lat - p_lat, 2) + Math.pow(lon - p_lon, 2));
        return dist <= maxDistance;
    });
    
    // Si no hay puntos cercanos, retornar null
    if (nearbyPoints.length === 0) {
        return null;
    }
    
    // Calcular la interpolación usando el inverso del cuadrado de la distancia
    for (const [p_lat, p_lon, value] of nearbyPoints) {
        const dist = Math.sqrt(Math.pow(lat - p_lat, 2) + Math.pow(lon - p_lon, 2));
        
        // Si encontramos un punto exacto, retornar su valor
        if (dist < 0.0001) {
            return value;
        }
        
        // Usar el inverso del cuadrado de la distancia como peso
        const weight = 1 / Math.pow(dist, 2);
        weightedSum += value * weight;
        weightSum += weight;
    }
    
    return weightedSum / weightSum;
}

// Función para encontrar las isolíneas más cercanas con mayor precisión
function findNearestIsolines(lat, lon, points, tolerance = 0.2) {
    const isolines = new Map();
    
    for (const [p_lat, p_lon, value] of points) {
        const dist = Math.sqrt(Math.pow(lat - p_lat, 2) + Math.pow(lon - p_lon, 2));
        if (dist <= tolerance) {
            if (!isolines.has(value) || dist < isolines.get(value)) {
                isolines.set(value, dist);
            }
        }
    }
    
    return Array.from(isolines.entries())
        .map(([value, dist]) => ({ value, distance: Number(dist.toFixed(3)) }))
        .sort((a, b) => a.distance - b.distance);
}

export function calculateSeismicParameters(lat, lng) {
    try {
        const ss = interpolateValue(lat, lng, POINTS_SS);
        const s1 = interpolateValue(lat, lng, POINTS_S1);
        
        if (ss === null || s1 === null) {
            throw new Error('No hay suficientes puntos cercanos para interpolar');
        }
        
        const isolinesSs = findNearestIsolines(lat, lng, POINTS_SS);
        const isolinesS1 = findNearestIsolines(lat, lng, POINTS_S1);
        
        return {
            ss: Number(ss.toFixed(3)),
            s1: Number(s1.toFixed(3)),
            isolinesSs,
            isolinesS1
        };
    } catch (error) {
        console.error('Error en el cálculo:', error);
        throw new Error('Error al calcular los parámetros. Por favor, verifique las coordenadas.');
    }
}

// Datos de los puntos (convertidos del código Python original)
const POINTS_SS = [
    [19.86, -73.37, 1.50],
    [19.84, -73.35, 1.50],
    [19.82, -73.32, 1.50],
    [19.80, -73.28, 1.50],
    [19.80, -73.25, 1.50],
    [19.80, -73.20, 1.50],
    [19.80, -73.15, 1.50],
    [19.81, -73.11, 1.50],
    [19.82, -73.07, 1.50],
    [19.84, -73.04, 1.50],
    [19.86, -73.02, 1.50],
    [19.87, -72.96, 1.50],
    [19.87, -72.94, 1.50],
    [19.89, -72.90, 1.50],
    [19.89, -72.85, 1.50],
    [19.89, -72.81, 1.50],
    [19.90, -72.77, 1.50],
    [19.89, -72.69, 1.50],
    [19.88, -72.65, 1.50],
    [19.88, -72.61, 1.50],
    [19.88, -72.57, 1.50],
    [19.87, -72.54, 1.50],
    [19.87, -72.51, 1.50],
    [19.86, -72.45, 1.50],
    [19.85, -72.42, 1.50],
    [19.84, -72.39, 1.50],
    [19.84, -72.36, 1.50],
    [19.82, -72.32, 1.50],
    [19.80, -72.29, 1.50],
    [19.78, -72.26, 1.50],
    [19.78, -72.23, 1.50],
    [19.75, -72.20, 1.50],
    [19.74, -72.16, 1.50],
    [19.70, -72.14, 1.50],
    [19.69, -72.13, 1.50],
    [19.67, -72.11, 1.50],
    [19.65, -72.11, 1.50],
    [19.64, -72.08, 1.50],
    [19.61, -72.06, 1.50],
    [19.58, -72.03, 1.50],
    [19.57, -72.01, 1.50],
    [19.60, -71.98, 1.50],
    [19.61, -71.95, 1.50],
    [19.63, -71.92, 1.50],
    [19.64, -71.89, 1.50],
    [19.66, -71.87, 1.50],
    [19.67, -71.84, 1.50],
    [19.67, -71.80, 1.50],
    [19.68, -71.77, 1.50],
    [19.69, -71.74, 1.50],
    [19.69, -71.70, 1.50],
    [19.69, -71.67, 1.50],
    [19.66, -71.63, 1.50],
    [19.65, -71.61, 1.50],
    [19.63, -71.57, 1.50],
    [19.62, -71.54, 1.50],
    [19.61, -71.52, 1.50],
    [19.61, -71.49, 1.50],
    [19.64, -71.44, 1.50],
    [19.65, -71.42, 1.50],
    [19.68, -71.39, 1.50],
    [19.69, -71.37, 1.50],
    [19.70, -71.32, 1.50],
    [19.70, -71.27, 1.50],
    [19.70, -71.24, 1.50],
    [19.70, -71.19, 1.50],
    [19.69, -71.18, 1.50],
    [19.69, -71.15, 1.50],
    [19.65, -71.11, 1.50],
    [19.63, -71.07, 1.50],
    [19.60, -71.06, 1.50],
    [19.56, -71.04, 1.50],
    [19.54, -71.02, 1.50],
    [19.54, -70.98, 1.50],
    [19.57, -70.95, 1.50],
    [19.60, -70.90, 1.50],
    [19.61, -70.86, 1.50],
    [19.62, -70.83, 1.50],
    [19.62, -70.80, 1.50],
    [19.60, -70.74, 1.50],
    [19.60, -70.70, 1.50],
    [19.59, -70.67, 1.50],
    [19.58, -70.65, 1.50],
    [19.57, -70.60, 1.50],
    [19.56, -70.57, 1.50],
    [19.55, -70.55, 1.50],
    [19.54, -70.52, 1.50],
    [19.54, -70.50, 1.50],
    [19.53, -70.45, 1.50],
    [19.53, -70.43, 1.50],
    [19.52, -70.39, 1.50],
    [19.52, -70.35, 1.50],
    [19.52, -70.32, 1.50],
    [19.51, -70.26, 1.50],
    [19.51, -70.23, 1.50],
    [19.50, -70.19, 1.50],
    [19.49, -70.14, 1.50],
    [19.49, -70.12, 1.50],
    [19.47, -70.10, 1.50],
    [19.44, -70.05, 1.50],
    [19.42, -70.03, 1.50],
    [19.41, -69.99, 1.50],
    [19.41, -69.95, 1.50],
    [19.41, -69.92, 1.50],
    [19.38, -69.87, 1.50],
    [19.38, -69.83, 1.50],
    
    [19.60, -72.98, 1.40],
    [19.63, -72.95, 1.40],
    [19.63, -72.92, 1.40],
    [19.63, -72.89, 1.40],
    [19.64, -72.86, 1.40],
    [19.66, -72.83, 1.40],
    [19.66, -72.80, 1.40],
    [19.66, -72.75, 1.40],
    [19.66, -72.71, 1.40],
    [19.65, -72.68, 1.40],
    [19.63, -72.63, 1.40],
    [19.62, -72.61, 1.40],
    [19.62, -72.56, 1.40],
    [19.62, -72.53, 1.40],
    [19.60, -72.50, 1.40],
    [19.60, -72.45, 1.40],
    [19.60, -72.42, 1.40],
    [19.60, -72.37, 1.40],
    [19.60, -72.32, 1.40],
    [19.60, -72.29, 1.40],
    [19.58, -72.25, 1.40],
    [19.57, -72.21, 1.40],
    [19.54, -72.17, 1.40],
    [19.53, -72.13, 1.40],
    [19.52, -72.10, 1.40],
    [19.50, -72.06, 1.40],
    [19.48, -72.01, 1.40],
    [19.49, -71.98, 1.40],
    [19.49, -71.94, 1.40],
    [19.51, -71.90, 1.40],
    [19.52, -71.88, 1.40],
    [19.51, -71.83, 1.40],
    [19.51, -71.78, 1.40],
    [19.51, -71.74, 1.40],
    [19.49, -71.68, 1.40],
    [19.50, -71.66, 1.40],
    [19.50, -71.61, 1.40],
    [19.48, -71.57, 1.40],
    [19.47, -71.53, 1.40],
    [19.47, -71.50, 1.40],
    [19.47, -71.47, 1.40],
    [19.48, -71.41, 1.40],
    [19.49, -71.38, 1.40],
    [19.49, -71.34, 1.40],
    [19.49, -71.29, 1.40],
    [19.49, -71.25, 1.40],
    [19.49, -71.21, 1.40],
    [19.49, -71.18, 1.40],
    [19.47, -71.11, 1.40],
    [19.46, -71.08, 1.40],
    [19.48, -71.15, 1.40],
    [19.45, -71.07, 1.40],
    [19.45, -71.02, 1.40],
    [19.45, -71.00, 1.40],
    [19.44, -70.96, 1.40],
    [19.44, -70.93, 1.40],
    [19.45, -70.89, 1.40],
    [19.45, -70.85, 1.40],
    [19.46, -70.81, 1.40],
    [19.46, -70.76, 1.40],
    [19.45, -70.73, 1.40],
    [19.43, -70.70, 1.40],
    [19.43, -70.67, 1.40],
    [19.41, -70.62, 1.40],
    [19.40, -70.60, 1.40],
    [19.39, -70.56, 1.40],
    [19.42, -70.66, 1.40],
    [19.39, -70.54, 1.40],
    [19.37, -70.51, 1.40],
    [19.37, -70.46, 1.40],
    [19.34, -70.44, 1.40],
    [19.33, -70.40, 1.40],
    [19.33, -70.37, 1.40],
    [19.32, -70.34, 1.40],
    [19.30, -70.30, 1.40],
    [19.28, -70.25, 1.40],
    [19.27, -70.23, 1.40],
    [19.25, -70.21, 1.40],
    [19.24, -70.19, 1.40],
    [19.22, -70.15, 1.40],
    [19.20, -70.12, 1.40],
    [19.17, -70.10, 1.40],
    [19.16, -70.07, 1.40],
    [19.15, -70.05, 1.40],
    [19.14, -70.02, 1.40],
    [19.14, -69.98, 1.40],
    [19.14, -69.94, 1.40],
    [19.14, -69.91, 1.40],
    [19.15, -69.87, 1.40],
    [19.17, -69.83, 1.40],
    [19.17, -69.79, 1.40],
    [19.17, -69.76, 1.40],
    [19.17, -69.72, 1.40],
    [19.17, -69.70, 1.40],
    [19.15, -69.67, 1.40],
    
    [19.52, -72.79, 1.30],
    [19.52, -72.74, 1.30],
    [19.52, -72.69, 1.30],
    [19.51, -72.64, 1.30],
    [19.50, -72.61, 1.30],
    [19.50, -72.58, 1.30],
    [19.47, -72.54, 1.30],
    [19.48, -72.50, 1.30],
    [19.48, -72.46, 1.30],
    [19.48, -72.40, 1.30],
    [19.47, -72.36, 1.30],
    [19.47, -72.32, 1.30],
    [19.47, -72.28, 1.30],
    [19.46, -72.24, 1.30],
    [19.46, -72.21, 1.30],
    [19.44, -72.17, 1.30],
    [19.42, -72.13, 1.30],
    [19.42, -72.11, 1.30],
    [19.42, -72.07, 1.30],
    [19.41, -72.04, 1.30],
    [19.42, -72.00, 1.30],
    [19.42, -71.96, 1.30],
    [19.42, -71.91, 1.30],
    [19.42, -71.89, 1.30],
    [19.41, -71.86, 1.30],
    [19.41, -71.79, 1.30],
    [19.41, -71.80, 1.30],
    [19.41, -71.73, 1.30],
    [19.40, -71.69, 1.30],
    [19.39, -71.66, 1.30],
    [19.39, -71.61, 1.30],
    [19.38, -71.59, 1.30],
    [19.37, -71.56, 1.30],
    [19.37, -71.50, 1.30],
    [19.37, -71.48, 1.30],
    [19.37, -71.45, 1.30],
    [19.37, -71.41, 1.30],
    [19.38, -71.38, 1.30],
    [19.39, -71.34, 1.30],
    [19.38, -71.30, 1.30],
    [19.39, -71.25, 1.30],
    [19.39, -71.22, 1.30],
    [19.37, -71.18, 1.30],
    [19.37, -71.14, 1.30],
    [19.37, -71.11, 1.30],
    [19.37, -71.07, 1.30],
    [19.35, -71.03, 1.30],
    [19.35, -71.00, 1.30],
    [19.35, -70.97, 1.30],
    [19.35, -70.94, 1.30],
    [19.34, -70.91, 1.30],
    [19.34, -70.87, 1.30],
    [19.33, -70.84, 1.30],
    [19.32, -70.79, 1.30],
    [19.31, -70.75, 1.30],
    [19.28, -70.73, 1.30],
    [19.28, -70.69, 1.30],
    [19.27, -70.66, 1.30],
    [19.26, -70.64, 1.30],
    [19.25, -70.60, 1.30],
    [19.23, -70.56, 1.30],
    [19.22, -70.54, 1.30],
    [19.20, -70.50, 1.30],
    [19.18, -70.46, 1.30],
    [19.16, -70.42, 1.30],
    [19.15, -70.38, 1.30],
    [19.15, -70.34, 1.30],
    [19.13, -70.30, 1.30],
    [19.12, -70.25, 1.30],
    [19.10, -70.22, 1.30],
    [19.08, -70.20, 1.30],
    [19.06, -70.14, 1.30],
    [19.05, -70.09, 1.30],
    [19.03, -70.09, 1.30],
    [19.02, -70.04, 1.30],
    [19.01, -70.02, 1.30],
    [19.00, -69.96, 1.30],
    [19.02, -69.93, 1.30],
    [19.03, -69.90, 1.30],
    [19.04, -69.86, 1.30],
    [19.05, -69.82, 1.30],
    [19.06, -69.79, 1.30],
    [19.05, -69.75, 1.30],
    [19.06, -69.72, 1.30],
    [19.05, -69.66, 1.30],
    [19.04, -69.62, 1.30],
    [19.02, -69.56, 1.30],
    [18.98, -69.52, 1.30],
    [18.99, -69.48, 1.30],
    [19.02, -69.40, 1.30],
    [19.06, -69.34, 1.30],
    [19.06, -69.37, 1.30],
    [19.02, -69.43, 1.30],
    [19.06, -69.31, 1.30],
    
    [19.42, -72.73, 1.10],
    [19.42, -72.70, 1.10],
    [19.41, -72.65, 1.10],
    [19.41, -72.61, 1.10],
    [19.41, -72.58, 1.10],
    [19.40, -72.53, 1.10],
    [19.39, -72.49, 1.10],
    [19.40, -72.46, 1.10],
    [19.40, -72.43, 1.10],
    [19.40, -72.41, 1.10],
    [19.40, -72.38, 1.10],
    [19.39, -72.34, 1.10],
    [19.39, -72.30, 1.10],
    [19.39, -72.27, 1.10],
    [19.38, -72.23, 1.10],
    [19.38, -72.21, 1.10],
    [19.37, -72.18, 1.10],
    [19.36, -72.14, 1.10],
    [19.35, -72.11, 1.10],
    [19.35, -72.06, 1.10],
    [19.35, -72.03, 1.10],
    [19.35, -72.00, 1.10],
    [19.33, -71.97, 1.10],
    [19.34, -71.94, 1.10],
    [19.33, -71.90, 1.10],
    [19.33, -71.88, 1.10],
    [19.33, -71.84, 1.10],
    [19.33, -71.81, 1.10],
    [19.33, -71.79, 1.10],
    [19.32, -71.76, 1.10],
    [19.31, -71.71, 1.10],
    [19.30, -71.68, 1.10],
    [19.31, -71.63, 1.10],
    [19.31, -71.58, 1.10],
    [19.29, -71.54, 1.10],
    [19.29, -71.49, 1.10],
    [19.28, -71.45, 1.10],
    [19.28, -71.39, 1.10],
    [19.28, -71.35, 1.10],
    [19.28, -71.31, 1.10],
    [19.27, -71.23, 1.10],
    [19.27, -71.20, 1.10],
    [19.27, -71.16, 1.10],
    [19.27, -71.11, 1.10],
    [19.27, -71.07, 1.10],
    [19.26, -71.03, 1.10],
    [19.26, -70.98, 1.10],
    [19.25, -70.93, 1.10],
    [19.24, -70.90, 1.10],
    [19.24, -70.87, 1.10],
    [19.24, -70.85, 1.10],
    [19.22, -70.80, 1.10],
    [19.19, -70.76, 1.10],
    [19.18, -70.73, 1.10],
    [19.16, -70.71, 1.10],
    [19.15, -70.68, 1.10],
    [19.14, -70.66, 1.10],
    [19.10, -70.62, 1.10],
    [19.09, -70.58, 1.10],
    [19.07, -70.54, 1.10],
    [19.04, -70.49, 1.10],
    [19.04, -70.45, 1.10],
    [19.02, -70.42, 1.10],
    [19.03, -70.36, 1.10],
    [19.00, -70.32, 1.10],
    [19.00, -70.29, 1.10],
    [18.98, -70.24, 1.10],
    [18.97, -70.20, 1.10],
    [18.96, -70.15, 1.10],
    [18.96, -70.11, 1.10],
    [18.96, -70.07, 1.10],
    [18.96, -70.03, 1.10],
    [18.95, -69.99, 1.10],
    [18.95, -69.94, 1.10],
    [18.96, -69.91, 1.10],
    [18.96, -69.85, 1.10],
    [18.97, -69.82, 1.10],
    [18.97, -69.77, 1.10],
    [18.96, -69.74, 1.10],
    [18.96, -69.68, 1.10],
    [18.96, -69.65, 1.10],
    [18.96, -69.60, 1.10],
    [18.96, -69.54, 1.10],
    [18.95, -69.50, 1.10],
    [18.95, -69.46, 1.10],
    [18.95, -69.41, 1.10],
    [18.95, -69.34, 1.10],
    [18.96, -69.30, 1.10],
    [18.95, -69.24, 1.10],
    [18.95, -69.19, 1.10],
    [18.95, -69.13, 1.10],
    [18.94, -69.10, 1.10],
    [18.92, -69.06, 1.10],
    [18.91, -69.02, 1.10],
    [18.88, -68.99, 1.10],
    [18.86, -68.95, 1.10],
    [18.84, -68.89, 1.10],
    [18.81, -68.86, 1.10],
    [18.78, -68.83, 1.10],
    [18.74, -68.81, 1.10],
    [18.70, -68.79, 1.10],
    [18.67, -68.78, 1.10],
    [18.60, -68.77, 1.10],
    [18.55, -68.76, 1.10],
    [18.51, -68.74, 1.10],
    [18.45, -68.73, 1.10],
    [18.42, -68.71, 1.10],
    [18.40, -68.68, 1.10],
    [18.36, -68.68, 1.10],
    [18.35, -68.65, 1.10],
    [18.32, -68.63, 1.10],
    
    [19.32, -72.75, 1.00],
    [19.32, -72.73, 1.00],
    [19.32, -72.66, 1.00],
    [19.33, -72.62, 1.00],
    [19.33, -72.59, 1.00],
    [19.33, -72.54, 1.00],
    [19.33, -72.49, 1.00],
    [19.33, -72.47, 1.00],
    [19.33, -72.42, 1.00],
    [19.32, -72.39, 1.00],
    [19.32, -72.36, 1.00],
    [19.32, -72.32, 1.00],
    [19.32, -72.29, 1.00],
    [19.32, -72.25, 1.00],
    [19.30, -72.22, 1.00],
    [19.30, -72.19, 1.00],
    [19.30, -72.15, 1.00],
    [19.29, -72.11, 1.00],
    [19.29, -72.08, 1.00],
    [19.29, -72.05, 1.00],
    [19.28, -72.02, 1.00],
    [19.27, -71.97, 1.00],
    [19.27, -71.93, 1.00],
    [19.26, -71.89, 1.00],
    [19.26, -71.86, 1.00],
    [19.24, -71.82, 1.00],
    [19.24, -71.79, 1.00],
    [19.24, -71.76, 1.00],
    [19.24, -71.70, 1.00],
    [19.23, -71.69, 1.00],
    [19.22, -71.64, 1.00],
    [19.22, -71.61, 1.00],
    [19.21, -71.58, 1.00],
    [19.21, -71.55, 1.00],
    [19.21, -71.51, 1.00],
    [19.21, -71.49, 1.00],
    [19.20, -71.43, 1.00],
    [19.20, -71.39, 1.00],
    [19.19, -71.35, 1.00],
    [19.19, -71.32, 1.00],
    [19.19, -71.29, 1.00],
    [19.19, -71.26, 1.00],
    [19.18, -71.19, 1.00],
    [19.17, -71.17, 1.00],
    [19.18, -71.09, 1.00],
    [19.18, -71.12, 1.00],
    [19.18, -71.05, 1.00],
    [19.17, -71.01, 1.00],
    [19.16, -70.97, 1.00],
    [19.15, -70.93, 1.00],
    [19.13, -70.91, 1.00],
    [19.13, -70.88, 1.00],
    [19.12, -70.85, 1.00],
    [19.09, -70.82, 1.00],
    [19.06, -70.78, 1.00],
    [19.05, -70.73, 1.00],
    [19.01, -70.68, 1.00],
    [18.98, -70.63, 1.00],
    [18.96, -70.58, 1.00],
    [18.95, -70.54, 1.00],
    [18.94, -70.48, 1.00],
    [18.92, -70.45, 1.00],
    [18.92, -70.40, 1.00],
    [18.92, -70.34, 1.00],
    [18.90, -70.30, 1.00],
    [18.89, -70.25, 1.00],
    [18.89, -70.21, 1.00],
    [18.89, -70.17, 1.00],
    [18.89, -70.14, 1.00],
    [18.89, -70.07, 1.00],
    [18.89, -70.02, 1.00],
    [18.90, -69.96, 1.00],
    [18.90, -69.93, 1.00],
    [18.89, -69.87, 1.00],
    [18.89, -69.82, 1.00],
    [18.89, -69.77, 1.00],
    [18.89, -69.71, 1.00],
    [18.90, -69.66, 1.00],
    [18.90, -69.61, 1.00],
    [18.90, -69.55, 1.00],
    [18.90, -69.51, 1.00],
    [18.88, -69.46, 1.00],
    [18.88, -69.41, 1.00],
    [18.87, -69.36, 1.00],
    [18.86, -69.32, 1.00],
    [18.83, -69.28, 1.00],
    [18.81, -69.22, 1.00],
    [18.78, -69.19, 1.00],
    [18.75, -69.17, 1.00],
    [18.72, -69.12, 1.00],
    [18.68, -69.10, 1.00],
    [18.64, -69.10, 1.00],
    [18.58, -69.10, 1.00],
    [18.54, -69.10, 1.00],
    [18.49, -69.09, 1.00],
    [18.45, -69.10, 1.00],
    [18.42, -69.10, 1.00],
    
    [19.20, -72.78, 0.90],
    [19.22, -72.75, 0.90],
    [19.22, -72.73, 0.90],
    [19.23, -72.70, 0.90],
    [19.24, -72.65, 0.90],
    [19.24, -72.63, 0.90],
    [19.24, -72.62, 0.90],
    [19.25, -72.56, 0.90],
    [19.25, -72.51, 0.90],
    [19.25, -72.48, 0.90],
    [19.24, -72.42, 0.90],
    [19.24, -72.36, 0.90],
    [19.24, -72.31, 0.90],
    [19.23, -72.29, 0.90],
    [19.23, -72.26, 0.90],
    [19.23, -72.22, 0.90],
    [19.22, -72.19, 0.90],
    [19.22, -72.14, 0.90],
    [19.22, -72.10, 0.90],
    [19.23, -72.07, 0.90],
    [19.22, -72.02, 0.90],
    [19.22, -72.00, 0.90],
    [19.20, -71.95, 0.90],
    [19.20, -71.91, 0.90],
    [19.18, -71.87, 0.90],
    [19.16, -71.83, 0.90],
    [19.16, -71.81, 0.90],
    [19.15, -71.79, 0.90],
    [19.14, -71.77, 0.90],
    [19.13, -71.74, 0.90],
    [19.13, -71.70, 0.90],
    [19.11, -71.65, 0.90],
    [19.11, -71.61, 0.90],
    [19.10, -71.59, 0.90],
    [19.09, -71.54, 0.90],
    [19.09, -71.52, 0.90],
    [19.09, -71.47, 0.90],
    [19.06, -71.44, 0.90],
    [19.06, -71.40, 0.90],
    [19.05, -71.36, 0.90],
    [19.03, -71.33, 0.90],
    [19.04, -71.28, 0.90],
    [19.04, -71.24, 0.90],
    [19.04, -71.19, 0.90],
    [19.05, -71.15, 0.90],
    [19.07, -71.10, 0.90],
    [19.08, -71.05, 0.90],
    [19.08, -71.02, 0.90],
    [19.07, -70.99, 0.90],
    [19.06, -70.96, 0.90],
    [19.04, -70.92, 0.90],
    [19.02, -70.89, 0.90],
    [18.98, -70.86, 0.90],
    [18.96, -70.81, 0.90],
    [18.92, -70.78, 0.90],
    [18.89, -70.76, 0.90],
    [18.87, -70.72, 0.90],
    [18.86, -70.67, 0.90],
    [18.86, -70.65, 0.90],
    [18.85, -70.60, 0.90],
    [18.85, -70.56, 0.90],
    [18.85, -70.54, 0.90],
    [18.84, -70.50, 0.90],
    [18.83, -70.47, 0.90],
    [18.82, -70.44, 0.90],
    [18.82, -70.39, 0.90],
    [18.80, -70.35, 0.90],
    [18.80, -70.32, 0.90],
    [18.80, -70.25, 0.90],
    [18.80, -70.21, 0.90],
    [18.81, -70.16, 0.90],
    [18.82, -70.14, 0.90],
    [18.82, -70.10, 0.90],
    [18.83, -70.07, 0.90],
    [18.83, -70.03, 0.90],
    [18.83, -70.00, 0.90],
    [18.83, -69.95, 0.90],
    [18.83, -69.91, 0.90],
    [18.83, -69.85, 0.90],
    [18.83, -69.83, 0.90],
    [18.83, -69.76, 0.90],
    [18.83, -69.71, 0.90],
    [18.83, -69.67, 0.90],
    [18.84, -69.62, 0.90],
    [18.84, -69.57, 0.90],
    [18.84, -69.52, 0.90],
    [18.84, -69.51, 0.90],
    [18.84, -69.47, 0.90],
    [18.83, -69.43, 0.90],
    [18.82, -69.41, 0.90],
    [18.80, -69.40, 0.90],
    [18.78, -69.37, 0.90],
    [18.77, -69.35, 0.90],
    [18.75, -69.32, 0.90],
    [18.72, -69.29, 0.90],
    [18.69, -69.28, 0.90],
    [18.66, -69.27, 0.90],
    [18.62, -69.25, 0.90],
    [18.59, -69.25, 0.90],
    [18.54, -69.25, 0.90],
    [18.53, -69.25, 0.90],
    [18.50, -69.25, 0.90],
    [18.48, -69.26, 0.90],
    
    [18.85, -72.44, 0.60],
    [18.85, -72.48, 0.60],
    [18.86, -72.56, 0.60],
    [18.87, -72.61, 0.60],
    [18.87, -72.53, 0.60],
    [18.96, -72.65, 0.60],
    [19.00, -72.66, 0.60],
    [19.02, -72.63, 0.60],
    [19.05, -72.62, 0.60],
    [19.06, -72.57, 0.60],
    [19.07, -72.53, 0.60],
    [19.08, -72.50, 0.60],
    [19.08, -72.46, 0.60],
    [19.06, -72.42, 0.60],
    [19.04, -72.37, 0.60],
    [19.02, -72.34, 0.60],
    [19.01, -72.32, 0.60],
    [18.98, -72.27, 0.60],
    [18.98, -72.26, 0.60],
    [18.98, -72.22, 0.60],
    [19.00, -72.19, 0.60],
    [19.02, -72.12, 0.60],
    [19.05, -72.10, 0.60],
    [19.05, -72.08, 0.60],
    [19.06, -72.03, 0.60],
    [19.03, -72.01, 0.60],
    [19.03, -71.99, 0.60],
    [19.01, -71.96, 0.60],
    [18.96, -71.94, 0.60],
    [18.93, -71.96, 0.60],
    [18.91, -71.99, 0.60],
    [18.87, -72.04, 0.60],
    [18.87, -72.09, 0.60],
    [18.87, -72.15, 0.60],
    [18.87, -72.22, 0.60],
    [18.86, -72.26, 0.60],
    [18.86, -72.29, 0.60],
    [18.85, -72.32, 0.60],
    [18.85, -72.37, 0.60],
    [18.84, -72.43, 0.60],
    [18.93, -72.63, 0.60],
    
    [19.11, -72.79, 0.70],
    [19.11, -72.76, 0.70],
    [19.12, -72.72, 0.70],
    [19.14, -72.69, 0.70],
    [19.14, -72.64, 0.70],
    [19.15, -72.61, 0.70],
    [19.15, -72.58, 0.70],
    [19.15, -72.53, 0.70],
    [19.16, -72.49, 0.70],
    [19.16, -72.44, 0.70],
    [19.16, -72.40, 0.70],
    [19.15, -72.38, 0.70],
    [19.15, -72.32, 0.70],
    [19.15, -72.29, 0.70],
    [19.14, -72.23, 0.70],
    [19.14, -72.21, 0.70],
    [19.14, -72.13, 0.70],
    [19.14, -72.10, 0.70],
    [19.15, -72.05, 0.70],
    [19.14, -72.00, 0.70],
    [19.13, -71.98, 0.70],
    [19.12, -71.94, 0.70],
    [19.11, -71.90, 0.70],
    [19.09, -71.87, 0.70],
    [19.07, -71.84, 0.70],
    [19.04, -71.82, 0.70],
    [19.01, -71.79, 0.70],
    [18.98, -71.78, 0.70],
    [18.94, -71.75, 0.70],
    [18.89, -71.73, 0.70],
    [18.87, -71.76, 0.70],
    [18.82, -71.77, 0.70],
    [18.80, -71.80, 0.70],
    [18.78, -71.85, 0.70],
    [18.73, -71.94, 0.70],
    [18.75, -71.89, 0.70],
    [18.71, -71.99, 0.70],
    [18.69, -72.01, 0.70],
    [18.66, -72.09, 0.70],
    [18.68, -72.07, 0.70],
    [18.68, -72.14, 0.70],
    [18.66, -72.20, 0.70],
    [18.66, -72.26, 0.70],
    [18.66, -72.32, 0.70],
    [18.66, -72.36, 0.70],
    
    [18.83, -71.17, 0.80],
    [18.87, -71.15, 0.80],
    [18.90, -71.14, 0.80],
    [18.93, -71.11, 0.80],
    [18.95, -71.09, 0.80],
    [18.96, -71.07, 0.80],
    [18.97, -71.06, 0.80],
    [18.98, -71.03, 0.80],
    [18.99, -71.03, 0.80],
    [18.97, -70.99, 0.80],
    [18.94, -70.98, 0.80],
    [18.91, -70.96, 0.80],
    [18.88, -70.93, 0.80],
    [18.87, -70.93, 0.80],
    [18.82, -70.91, 0.80],
    [18.79, -70.91, 0.80],
    [18.77, -70.94, 0.80],
    [18.75, -70.97, 0.80],
    [18.74, -71.01, 0.80],
    [18.75, -71.09, 0.80],
    [18.75, -71.07, 0.80],
    [18.77, -71.12, 0.80],
    [18.78, -71.16, 0.80],
    [18.82, -71.16, 0.80],

    [18.51, -68.36, 1.15],
    [18.51, -68.40, 1.15],
    [18.51, -68.43, 1.15],
    [18.51, -68.48, 1.15],
    [18.53, -68.49, 1.15],
    [18.56, -68.51, 1.15],
    [18.60, -68.52, 1.15],
    [18.63, -68.53, 1.15],
    [18.66, -68.53, 1.15],
    [18.69, -68.53, 1.15],
    [18.72, -68.53, 1.15],
    [18.74, -68.52, 1.15],
    
    [19.23, -69.35, 1.40],
    [19.24, -69.31, 1.40],
    [19.26, -69.28, 1.40],
    [19.28, -69.24, 1.40],
    [19.30, -69.22, 1.40],
    [19.32, -69.20, 1.40],
    
    [18.53, -73.55, 0.90],
    [18.52, -73.50, 0.90],
    [18.51, -73.46, 0.90],
    [18.51, -73.42, 0.90],
    [18.51, -73.38, 0.90],
    [18.51, -73.35, 0.90],
    [18.50, -73.28, 0.90],
    [18.50, -73.25, 0.90],
    [18.50, -73.23, 0.90],
    [18.50, -73.20, 0.90],
    [18.48, -73.16, 0.90],
    [18.49, -73.13, 0.90],
    [18.49, -73.09, 0.90],
    [18.49, -73.05, 0.90],
    [18.49, -73.00, 0.90],
    [18.49, -72.97, 0.90],
    [18.48, -72.94, 0.90],
    [18.45, -72.91, 0.90],
    [18.44, -72.86, 0.90],
    [18.43, -72.84, 0.90],
    [18.42, -72.80, 0.90],
    [18.42, -72.77, 0.90],
    [18.42, -72.73, 0.90],
    [18.42, -72.71, 0.90],
    [18.42, -72.66, 0.90],
    [18.43, -72.63, 0.90],
    [18.45, -72.59, 0.90],
    [18.47, -72.57, 0.90],
    [18.47, -72.54, 0.90],
    [18.46, -72.51, 0.90],
    [18.46, -72.48, 0.90],
    [18.44, -72.46, 0.90],
    [18.43, -72.42, 0.90],
    [18.42, -72.39, 0.90],
    [18.41, -72.37, 0.90],
    [18.40, -72.33, 0.90],
    [18.39, -72.30, 0.90],
    [18.39, -72.27, 0.90],
    [18.38, -72.22, 0.90],
    [18.38, -72.20, 0.90],
    [18.40, -72.17, 0.90],
    [18.42, -72.12, 0.90],
    [18.42, -72.11, 0.90],
    [18.43, -72.08, 0.90],
    [18.44, -72.04, 0.90],
    [18.45, -72.01, 0.90],
    [18.46, -72.00, 0.90],
    [18.46, -71.96, 0.90],
    [18.44, -71.91, 0.90],
    [18.42, -71.89, 0.90],
    [18.40, -71.85, 0.90],
    [18.39, -71.82, 0.90],
    [18.39, -71.79, 0.90],
    [18.39, -71.75, 0.90],
    [18.39, -71.70, 0.90],
    [18.39, -71.67, 0.90],
    [18.41, -71.61, 0.90],
    [18.42, -71.59, 0.90],
    [18.45, -71.55, 0.90],
    [18.46, -71.52, 0.90],
    [18.47, -71.48, 0.90],
    [18.48, -71.43, 0.90],
    [18.48, -71.36, 0.90],
    [18.48, -71.38, 0.90],
    [18.48, -71.31, 0.90],
    [18.48, -71.25, 0.90],
    [18.48, -71.21, 0.90],
    [18.49, -71.16, 0.90],
    [18.52, -71.11, 0.90],
    [18.53, -71.07, 0.90],
    [18.54, -71.04, 0.90],
    [18.54, -70.98, 0.90],
    [18.54, -70.92, 0.90],
    [18.53, -70.86, 0.90],
    [18.53, -70.84, 0.90],
    [18.52, -70.82, 0.90],
    [18.51, -70.76, 0.90],
    [18.51, -70.73, 0.90],
    [18.51, -70.66, 0.90],
    [18.51, -70.63, 0.90],
    [18.51, -70.57, 0.90],
    [18.51, -70.52, 0.90],
    [18.50, -70.49, 0.90],
    [18.47, -70.47, 0.90],
    [18.43, -70.43, 0.90],
    [18.42, -70.40, 0.90],
    [18.41, -70.37, 0.90],
    [18.38, -70.32, 0.90],
    [18.38, -70.30, 0.90],
    [18.36, -70.27, 0.90],
    [18.34, -70.23, 0.90],
    [18.33, -70.19, 0.90],
    [18.33, -70.17, 0.90],
    [18.32, -70.14, 0.90],
    [18.30, -70.12, 0.90],
    
    [18.53, -69.93, 0.62],
    [18.53, -69.98, 0.62],
    [18.55, -70.02, 0.62],
    [18.60, -70.03, 0.62],
    [18.64, -70.01, 0.62],
    [18.66, -69.98, 0.62],
    [18.67, -69.95, 0.62],
    [18.67, -69.91, 0.62],
    [18.64, -69.86, 0.62],
    [18.63, -69.83, 0.62],
    [18.64, -69.80, 0.62],
    [18.64, -69.73, 0.62],
    [18.64, -69.69, 0.62],
    [18.67, -69.62, 0.62],
    [18.69, -69.60, 0.62],
    [18.69, -69.56, 0.62],
    [18.72, -69.53, 0.62],
    [18.74, -69.51, 0.62],
    [18.74, -69.48, 0.62],
    [18.69, -69.48, 0.62],
    [18.65, -69.47, 0.62],
    [18.60, -69.49, 0.62],
    [18.57, -69.49, 0.62],
    [18.54, -69.50, 0.62],
    [18.54, -69.51, 0.62],
    [18.54, -69.57, 0.62],
    [18.54, -69.60, 0.62],
    [18.53, -69.67, 0.62],
    [18.52, -69.71, 0.62],
    [18.52, -69.75, 0.62],
    [18.51, -69.78, 0.62],
    [18.51, -69.82, 0.62],
    [18.51, -69.85, 0.62],
    [18.52, -69.89, 0.62],
    [18.52, -69.90, 0.62],
    
    [18.67, -74.36, 0.70],
    [18.62, -74.34, 0.70],
    [18.59, -74.33, 0.70],
    [18.56, -74.33, 0.70],
    [18.53, -74.31, 0.70],
    [18.50, -74.30, 0.70],
    [18.45, -74.29, 0.70],
    [18.43, -74.27, 0.70],
    [18.40, -74.25, 0.70],
    [18.37, -74.24, 0.70],
    [18.34, -74.21, 0.70],
    [18.31, -74.19, 0.70],
    [18.28, -74.17, 0.70],
    [18.26, -74.14, 0.70],
    [18.25, -74.12, 0.70],
    [18.24, -74.09, 0.70],
    [18.22, -74.08, 0.70],
    [18.20, -74.06, 0.70],
    [18.18, -74.03, 0.70],
    [18.17, -74.02, 0.70],
    [18.15, -73.98, 0.70],
    [18.14, -73.97, 0.70],
    [18.11, -73.95, 0.70],
    [18.11, -73.92, 0.70],
    [18.09, -73.90, 0.70],
    [18.07, -73.88, 0.70],
    [18.06, -73.87, 0.70],
    [18.06, -73.85, 0.70],
    [18.04, -73.83, 0.70],
    
    [18.60, -74.02, 0.90],
    [18.56, -73.99, 0.90],
    [18.52, -73.99, 0.90],
    [18.51, -73.97, 0.90],
    [18.47, -73.91, 0.90],
    [18.46, -73.87, 0.90],
    [18.44, -73.83, 0.90],
    [18.42, -73.79, 0.90],
    [18.41, -73.77, 0.90],
    [18.39, -73.73, 0.90],
    [18.36, -73.71, 0.90],
    [18.33, -73.69, 0.90],
    [18.30, -73.66, 0.90],
    [18.27, -73.63, 0.90],
    
    [18.28, -73.40, 0.94],
    [18.29, -73.39, 0.94],
    [18.30, -73.36, 0.94],
    [18.31, -73.35, 0.94],
    [18.32, -73.31, 0.94],
    [18.32, -73.27, 0.94],
    [18.33, -73.25, 0.94],
    [18.33, -73.20, 0.94],
    [18.33, -73.19, 0.94],
    [18.34, -73.15, 0.94],
    [18.35, -73.13, 0.94],
    [18.35, -73.09, 0.94],
    [18.34, -73.05, 0.94],
    [18.34, -73.02, 0.94],
    [18.33, -72.98, 0.94],
    [18.32, -72.95, 0.94],
    [18.30, -72.91, 0.94],
    [18.26, -72.86, 0.94],
    [18.24, -72.87, 0.94],
    [18.22, -72.90, 0.94],
    [18.20, -72.93, 0.94],
    [18.18, -72.95, 0.94],
    
    [17.76, -71.61, 0.90],
    [17.82, -71.62, 0.90],
    [17.86, -71.64, 0.90],
    [17.90, -71.64, 0.90],
    [17.93, -71.64, 0.90],
    [17.97, -71.65, 0.90],
    [17.99, -71.66, 0.90],
    [18.00, -71.70, 0.90],
    [18.04, -71.74, 0.90],
    
    [17.84, -71.30, 0.94],
    [17.90, -71.28, 0.94],
    [17.96, -71.27, 0.94],
    [17.98, -71.24, 0.94],
    [18.02, -71.22, 0.94],
    [18.06, -71.20, 0.94],
    [18.11, -71.18, 0.94],
    [18.13, -71.17, 0.94],
    [18.15, -71.14, 0.94],
    [18.17, -71.10, 0.94],
    [18.18, -71.09, 0.94],
    ];
      
    
    const POINTS_S1 = [
       
    [19.81, -73.36, 0.76],
    [19.79, -73.31, 0.76],
    [19.77, -73.26, 0.76],
    [19.77, -73.20, 0.76],
    [19.76, -73.13, 0.76],
    [19.78, -73.07, 0.76],
    [19.79, -73.02, 0.76],
    [19.79, -72.96, 0.76],
    [19.81, -72.90, 0.76],
    [19.82, -72.85, 0.76],
    [19.83, -72.79, 0.76],
    [19.83, -72.73, 0.76],
    [19.83, -72.69, 0.76],
    [19.83, -72.65, 0.76],
    [19.83, -72.60, 0.76],
    [19.83, -72.55, 0.76],
    [19.81, -72.50, 0.76],
    [19.79, -72.45, 0.76],
    [19.79, -72.41, 0.76],
    [19.77, -72.35, 0.76],
    [19.76, -72.29, 0.76],
    [19.74, -72.25, 0.76],
    [19.72, -72.18, 0.76],
    [19.70, -72.16, 0.76],
    [19.64, -72.11, 0.76],
    [19.60, -72.08, 0.76],
    [19.56, -72.05, 0.76],
    [19.55, -72.02, 0.76],
    [19.55, -71.96, 0.76],
    [19.57, -71.90, 0.76],
    [19.59, -71.82, 0.76],
    [19.61, -71.74, 0.76],
    [19.61, -71.71, 0.76],
    [19.60, -71.65, 0.76],
    [19.57, -71.60, 0.76],
    [19.55, -71.54, 0.76],
    [19.55, -71.49, 0.76],
    [19.57, -71.42, 0.76],
    [19.60, -71.37, 0.76],
    [19.60, -71.31, 0.76],
    [19.61, -71.24, 0.76],
    [19.61, -71.19, 0.76],
    [19.60, -71.15, 0.76],
    [19.58, -71.11, 0.76],
    [19.56, -71.07, 0.76],
    [19.54, -71.03, 0.76],
    [19.54, -71.00, 0.76],
    [19.54, -70.96, 0.76],
    [19.56, -70.92, 0.76],
    [19.56, -70.89, 0.76],
    [19.58, -70.85, 0.76],
    [19.59, -70.80, 0.76],
    [19.59, -70.75, 0.76],
    [19.59, -70.72, 0.76],
    [19.59, -70.68, 0.76],
    [19.57, -70.64, 0.76],
    [19.56, -70.61, 0.76],
    [19.54, -70.56, 0.76],
    [19.52, -70.53, 0.76],
    [19.51, -70.50, 0.76],
    [19.51, -70.43, 0.76],
    [19.51, -70.46, 0.76],
    [19.51, -70.38, 0.76],
    [19.51, -70.33, 0.76],
    [19.46, -70.28, 0.76],
    [19.50, -70.31, 0.76],
    [19.46, -70.24, 0.76],
    [19.44, -70.20, 0.76],
    [19.43, -70.16, 0.76],
    [19.41, -70.12, 0.76],
    [19.39, -70.08, 0.76],
    [19.37, -70.05, 0.76],
    [19.37, -70.01, 0.76],
    [19.35, -69.96, 0.76],
    [19.34, -69.93, 0.76],
    [19.33, -69.90, 0.76],
    [19.32, -69.84, 0.76],
    [19.32, -69.81, 0.76],
    [19.32, -69.78, 0.76],
    [19.32, -69.73, 0.76],
    [19.32, -69.70, 0.76],
    [19.33, -69.66, 0.76],
    [19.33, -69.63, 0.76],
    [19.33, -69.60, 0.76],
    [19.33, -69.58, 0.76],
    [19.34, -69.53, 0.76],
    
    [19.73, -72.04, 0.77],
    [19.69, -72.03, 0.77],
    [19.65, -72.03, 0.77],
    [19.62, -72.01, 0.77],
    [19.60, -72.00, 0.77],
    [19.64, -71.96, 0.77],
    [19.65, -71.91, 0.77],
    [19.67, -71.86, 0.77],
    [19.69, -71.81, 0.77],
    [19.70, -71.76, 0.77],
    [19.70, -71.72, 0.77],
    [19.70, -71.67, 0.77],
    [19.68, -71.62, 0.77],
    [19.66, -71.60, 0.77],
    [19.63, -71.53, 0.77],
    [19.63, -71.49, 0.77],
    [19.65, -71.45, 0.77],
    [19.68, -71.42, 0.77],
    [19.70, -71.38, 0.77],
    [19.70, -71.34, 0.77],
    [19.70, -71.31, 0.77],
    [19.70, -71.26, 0.77],
    [19.72, -71.23, 0.77],
    [19.72, -71.19, 0.77],
    [19.70, -71.16, 0.77],
    [19.69, -71.13, 0.77],
    [19.66, -71.08, 0.77],
    [19.63, -71.06, 0.77],
    [19.62, -71.04, 0.77],
    [19.61, -71.01, 0.77],
    [19.61, -70.97, 0.77],
    [19.65, -70.93, 0.77],
    [19.66, -70.90, 0.77],
    [19.70, -70.86, 0.77],
    [19.70, -70.80, 0.77],
    [19.70, -70.77, 0.77],
    [19.71, -70.73, 0.77],
    [19.70, -70.69, 0.77],
    [19.69, -70.66, 0.77],
    [19.69, -70.62, 0.77],
    [19.68, -70.61, 0.77],
    [19.67, -70.58, 0.77],
    [19.64, -70.54, 0.77],
    [19.61, -70.53, 0.77],
    [19.60, -70.51, 0.77],
    [19.60, -70.49, 0.77],
    [19.60, -70.45, 0.77],
    [19.60, -70.42, 0.77],
    [19.61, -70.39, 0.77],
    [19.62, -70.36, 0.77],
    [19.62, -70.31, 0.77],
    [19.62, -70.26, 0.77],
    [19.63, -70.23, 0.77],
    [19.64, -70.19, 0.77],
    [19.64, -70.15, 0.77],
    [19.63, -70.11, 0.77],
    [19.62, -70.08, 0.77],
    [19.61, -70.07, 0.77],
    [19.60, -70.04, 0.77],
    [19.57, -70.00, 0.77],
    [19.56, -69.97, 0.77],
    [19.53, -69.95, 0.77],
    [19.51, -69.91, 0.77],
    [19.48, -69.89, 0.77],
    [19.45, -69.86, 0.77],
    
    [19.60, -72.94, 0.70],
    [19.60, -72.91, 0.70],
    [19.61, -72.90, 0.70],
    [19.61, -72.86, 0.70],
    [19.62, -72.83, 0.70],
    [19.62, -72.79, 0.70],
    [19.63, -72.76, 0.70],
    [19.63, -72.73, 0.70],
    [19.63, -72.70, 0.70],
    [19.62, -72.67, 0.70],
    [19.61, -72.63, 0.70],
    [19.60, -72.60, 0.70],
    [19.59, -72.57, 0.70],
    [19.59, -72.54, 0.70],
    [19.59, -72.51, 0.70],
    [19.59, -72.48, 0.70],
    [19.59, -72.45, 0.70],
    [19.59, -72.43, 0.70],
    [19.59, -72.40, 0.70],
    [19.59, -72.37, 0.70],
    [19.58, -72.34, 0.70],
    [19.57, -72.29, 0.70],
    [19.57, -72.26, 0.70],
    [19.56, -72.23, 0.70],
    [19.54, -72.21, 0.70],
    [19.53, -72.18, 0.70],
    [19.53, -72.15, 0.70],
    [19.51, -72.12, 0.70],
    [19.51, -72.10, 0.70],
    [19.50, -72.08, 0.70],
    [19.49, -72.05, 0.70],
    [19.49, -72.02, 0.70],
    [19.48, -72.00, 0.70],
    [19.48, -71.97, 0.70],
    [19.46, -71.94, 0.70],
    [19.46, -71.92, 0.70],
    [19.47, -71.89, 0.70],
    [19.48, -71.87, 0.70],
    [19.48, -71.85, 0.70],
    [19.48, -71.83, 0.70],
    [19.48, -71.81, 0.70],
    [19.48, -71.77, 0.70],
    [19.48, -71.74, 0.70],
    [19.48, -71.70, 0.70],
    [19.48, -71.67, 0.70],
    [19.46, -71.65, 0.70],
    [19.46, -71.61, 0.70],
    [19.46, -71.57, 0.70],
    [19.46, -71.53, 0.70],
    [19.46, -71.49, 0.70],
    [19.46, -71.46, 0.70],
    [19.46, -71.41, 0.70],
    [19.46, -71.38, 0.70],
    [19.47, -71.34, 0.70],
    [19.47, -71.31, 0.70],
    [19.47, -71.28, 0.70],
    [19.47, -71.24, 0.70],
    [19.46, -71.20, 0.70],
    [19.46, -71.17, 0.70],
    [19.46, -71.14, 0.70],
    [19.46, -71.11, 0.70],
    [19.46, -71.07, 0.70],
    [19.46, -71.02, 0.70],
    [19.46, -71.00, 0.70],
    [19.45, -70.96, 0.70],
    [19.45, -70.93, 0.70],
    [19.45, -70.91, 0.70],
    [19.45, -70.88, 0.70],
    [19.45, -70.86, 0.70],
    [19.45, -70.84, 0.70],
    [19.45, -70.80, 0.70],
    [19.45, -70.78, 0.70],
    [19.45, -70.75, 0.70],
    [19.45, -70.71, 0.70],
    [19.42, -70.66, 0.70],
    [19.43, -70.68, 0.70],
    [19.42, -70.63, 0.70],
    [19.41, -70.60, 0.70],
    [19.40, -70.57, 0.70],
    [19.39, -70.54, 0.70],
    [19.37, -70.49, 0.70],
    [19.37, -70.47, 0.70],
    [19.36, -70.43, 0.70],
    [19.34, -70.40, 0.70],
    [19.33, -70.36, 0.70],
    [19.32, -70.34, 0.70],
    [19.30, -70.31, 0.70],
    [19.30, -70.29, 0.70],
    [19.28, -70.25, 0.70],
    [19.26, -70.22, 0.70],
    [19.25, -70.20, 0.70],
    [19.23, -70.19, 0.70],
    [19.22, -70.16, 0.70],
    [19.20, -70.14, 0.70],
    [19.18, -70.11, 0.70],
    [19.17, -70.09, 0.70],
    [19.15, -70.08, 0.70],
    [19.12, -70.04, 0.70],
    [19.10, -70.02, 0.70],
    [19.10, -70.00, 0.70],
    [19.09, -69.97, 0.70],
    [19.10, -69.93, 0.70],
    [19.14, -69.90, 0.70],
    [19.13, -69.87, 0.70],
    [19.14, -69.83, 0.70],
    [19.15, -69.79, 0.70],
    [19.14, -69.75, 0.70],
    [19.14, -69.72, 0.70],
    [19.14, -69.68, 0.70],
    [19.14, -69.63, 0.70],
    [19.12, -69.60, 0.70],
    [19.11, -69.57, 0.70],
    [19.09, -69.53, 0.70],
    [19.08, -69.52, 0.70],
    [19.08, -69.49, 0.70],
    [19.08, -69.46, 0.70],
    [19.10, -69.43, 0.70],
    [19.12, -69.40, 0.70],
    
    [19.42, -72.72, 0.60],
    [19.42, -72.70, 0.60],
    [19.42, -72.67, 0.60],
    [19.41, -72.63, 0.60],
    [19.41, -72.60, 0.60],
    [19.41, -72.57, 0.60],
    [19.40, -72.54, 0.60],
    [19.39, -72.50, 0.60],
    [19.38, -72.46, 0.60],
    [19.38, -72.43, 0.60],
    [19.38, -72.40, 0.60],
    [19.37, -72.36, 0.60],
    [19.37, -72.33, 0.60],
    [19.37, -72.31, 0.60],
    [19.37, -72.28, 0.60],
    [19.37, -72.25, 0.60],
    [19.36, -72.22, 0.60],
    [19.36, -72.21, 0.60],
    [19.34, -72.15, 0.60],
    [19.35, -72.13, 0.60],
    [19.34, -72.08, 0.60],
    [19.33, -72.04, 0.60],
    [19.32, -72.01, 0.60],
    [19.32, -71.99, 0.60],
    [19.32, -71.97, 0.60],
    [19.32, -71.94, 0.60],
    [19.31, -71.91, 0.60],
    [19.31, -71.88, 0.60],
    [19.31, -71.85, 0.60],
    [19.31, -71.80, 0.60],
    [19.30, -71.81, 0.60],
    [19.31, -71.78, 0.60],
    [19.29, -71.74, 0.60],
    [19.29, -71.71, 0.60],
    [19.29, -71.67, 0.60],
    [19.28, -71.63, 0.60],
    [19.28, -71.57, 0.60],
    [19.28, -71.53, 0.60],
    [19.28, -71.49, 0.60],
    [19.28, -71.46, 0.60],
    [19.28, -71.43, 0.60],
    [19.28, -71.40, 0.60],
    [19.28, -71.36, 0.60],
    [19.28, -71.31, 0.60],
    [19.28, -71.34, 0.60],
    [19.28, -71.27, 0.60],
    [19.28, -71.24, 0.60],
    [19.28, -71.21, 0.60],
    [19.28, -71.17, 0.60],
    [19.28, -71.12, 0.60],
    [19.28, -71.09, 0.60],
    [19.28, -71.05, 0.60],
    [19.28, -70.98, 0.60],
    [19.28, -71.04, 0.60],
    [19.27, -70.98, 0.60],
    [19.27, -70.95, 0.60],
    [19.26, -70.93, 0.60],
    [19.25, -70.90, 0.60],
    [19.24, -70.87, 0.60],
    [19.23, -70.84, 0.60],
    [19.23, -70.80, 0.60],
    [19.21, -70.75, 0.60],
    [19.20, -70.73, 0.60],
    [19.18, -70.70, 0.60],
    [19.18, -70.67, 0.60],
    [19.17, -70.63, 0.60],
    [19.15, -70.60, 0.60],
    [19.14, -70.57, 0.60],
    [19.12, -70.54, 0.60],
    [19.10, -70.51, 0.60],
    [19.09, -70.49, 0.60],
    [19.08, -70.46, 0.60],
    [19.07, -70.44, 0.60],
    [19.06, -70.40, 0.60],
    [19.05, -70.37, 0.60],
    [19.04, -70.36, 0.60],
    [19.04, -70.33, 0.60],
    [19.04, -70.29, 0.60],
    [19.00, -70.24, 0.60],
    [19.02, -70.26, 0.60],
    [19.00, -70.20, 0.60],
    [18.99, -70.15, 0.60],
    [18.98, -70.15, 0.60],
    [18.97, -70.11, 0.60],
    [18.95, -70.06, 0.60],
    [18.95, -70.02, 0.60],
    [18.95, -69.98, 0.60],
    [18.95, -69.95, 0.60],
    [18.95, -69.92, 0.60],
    [18.95, -69.89, 0.60],
    [18.96, -69.86, 0.60],
    [18.97, -69.82, 0.60],
    [18.97, -69.79, 0.60],
    [18.97, -69.75, 0.60],
    [18.97, -69.72, 0.60],
    [18.97, -69.69, 0.60],
    [18.97, -69.64, 0.60],
    [18.97, -69.63, 0.60],
    [18.96, -69.60, 0.60],
    [18.96, -69.57, 0.60],
    [18.95, -69.53, 0.60],
    [18.95, -69.52, 0.60],
    [18.95, -69.49, 0.60],
    [18.95, -69.47, 0.60],
    [18.95, -69.45, 0.60],
    [18.95, -69.42, 0.60],
    [18.95, -69.39, 0.60],
    [18.95, -69.35, 0.60],
    [18.95, -69.32, 0.60],
    [18.94, -69.30, 0.60],
    [18.94, -69.27, 0.60],
    [18.94, -69.25, 0.60],
    [18.94, -69.20, 0.60],
    [18.92, -69.16, 0.60],
    [18.91, -69.11, 0.60],
    [18.90, -69.07, 0.60],
    [18.90, -69.06, 0.60],
    [18.86, -69.03, 0.60],
    [18.85, -68.99, 0.60],
    [18.84, -68.98, 0.60],
    [18.81, -68.96, 0.60],
    [18.80, -68.94, 0.60],
    [18.76, -68.92, 0.60],
    [18.72, -68.91, 0.60],
    [18.70, -68.89, 0.60],
    [18.66, -68.88, 0.60],
    [18.63, -68.87, 0.60],
    [18.58, -68.84, 0.60],
    [18.53, -68.84, 0.60],
    [18.49, -68.81, 0.60],
    [18.45, -68.79, 0.60],
    [18.43, -68.78, 0.60],
    [18.40, -68.76, 0.60],
    [18.39, -68.73, 0.60],
    [18.36, -68.70, 0.60],
    [18.35, -68.67, 0.60],
    [18.34, -68.64, 0.60],
    [18.32, -68.62, 0.60],
    
    [19.26, -72.79, 0.50],
    [19.26, -72.77, 0.50],
    [19.26, -72.74, 0.50],
    [19.25, -72.72, 0.50],
    [19.25, -72.68, 0.50],
    [19.25, -72.66, 0.50],
    [19.25, -72.63, 0.50],
    [19.25, -72.59, 0.50],
    [19.25, -72.57, 0.50],
    [19.25, -72.54, 0.50],
    [19.25, -72.50, 0.50],
    [19.25, -72.49, 0.50],
    [19.25, -72.45, 0.50],
    [19.25, -72.42, 0.50],
    [19.24, -72.39, 0.50],
    [19.23, -72.36, 0.50],
    [19.23, -72.32, 0.50],
    [19.22, -72.28, 0.50],
    [19.22, -72.26, 0.50],
    [19.21, -72.22, 0.50],
    [19.20, -72.17, 0.50],
    [19.19, -72.14, 0.50],
    [19.19, -72.10, 0.50],
    [19.19, -72.07, 0.50],
    [19.19, -72.03, 0.50],
    [19.18, -72.01, 0.50],
    [19.18, -71.99, 0.50],
    [19.18, -71.94, 0.50],
    [19.18, -71.92, 0.50],
    [19.17, -71.90, 0.50],
    [19.16, -71.88, 0.50],
    [19.15, -71.85, 0.50],
    [19.15, -71.83, 0.50],
    [19.14, -71.81, 0.50],
    [19.14, -71.78, 0.50],
    [19.13, -71.76, 0.50],
    [19.13, -71.72, 0.50],
    [19.13, -71.70, 0.50],
    [19.12, -71.67, 0.50],
    [19.12, -71.64, 0.50],
    [19.10, -71.63, 0.50],
    [19.11, -71.60, 0.50],
    [19.11, -71.56, 0.50],
    [19.11, -71.53, 0.50],
    [19.11, -71.51, 0.50],
    [19.11, -71.48, 0.50],
    [19.10, -71.44, 0.50],
    [19.10, -71.40, 0.50],
    [19.10, -71.36, 0.50],
    [19.09, -71.32, 0.50],
    [19.09, -71.31, 0.50],
    [19.09, -71.27, 0.50],
    [19.09, -71.24, 0.50],
    [19.09, -71.21, 0.50],
    [19.09, -71.18, 0.50],
    [19.09, -71.16, 0.50],
    [19.09, -71.13, 0.50],
    [19.09, -71.09, 0.50],
    [19.09, -71.05, 0.50],
    [19.10, -71.03, 0.50],
    [19.10, -71.02, 0.50],
    [19.09, -70.98, 0.50],
    [19.09, -70.95, 0.50],
    [19.07, -70.93, 0.50],
    [19.05, -70.91, 0.50],
    [19.05, -70.88, 0.50],
    [19.03, -70.85, 0.50],
    [19.02, -70.83, 0.50],
    [19.00, -70.81, 0.50],
    [19.00, -70.79, 0.50],
    [18.98, -70.77, 0.50],
    [18.97, -70.76, 0.50],
    [18.95, -70.73, 0.50],
    [18.94, -70.71, 0.50],
    [18.93, -70.69, 0.50],
    [18.92, -70.67, 0.50],
    [18.90, -70.64, 0.50],
    [18.89, -70.61, 0.50],
    [18.88, -70.56, 0.50],
    [18.87, -70.53, 0.50],
    [18.86, -70.51, 0.50],
    [18.86, -70.48, 0.50],
    [18.86, -70.43, 0.50],
    [18.86, -70.40, 0.50],
    [18.85, -70.36, 0.50],
    [18.84, -70.31, 0.50],
    [18.83, -70.29, 0.50],
    [18.82, -70.25, 0.50],
    [18.82, -70.22, 0.50],
    [18.82, -70.19, 0.50],
    [18.83, -70.16, 0.50],
    [18.83, -70.12, 0.50],
    [18.83, -70.09, 0.50],
    [18.85, -70.04, 0.50],
    [18.85, -70.01, 0.50],
    [18.85, -69.97, 0.50],
    [18.85, -69.94, 0.50],
    [18.85, -69.90, 0.50],
    [18.85, -69.88, 0.50],
    [18.84, -69.85, 0.50],
    [18.84, -69.82, 0.50],
    [18.84, -69.78, 0.50],
    [18.85, -69.75, 0.50],
    [18.85, -69.72, 0.50],
    [18.86, -69.69, 0.50],
    [18.86, -69.66, 0.50],
    [18.86, -69.63, 0.50],
    [18.86, -69.61, 0.50],
    [18.86, -69.58, 0.50],
    [18.86, -69.57, 0.50],
    [18.86, -69.53, 0.50],
    [18.86, -69.49, 0.50],
    [18.85, -69.44, 0.50],
    [18.84, -69.43, 0.50],
    [18.83, -69.39, 0.50],
    [18.81, -69.35, 0.50],
    [18.80, -69.32, 0.50],
    [18.78, -69.31, 0.50],
    [18.77, -69.29, 0.50],
    [18.76, -69.28, 0.50],
    [18.73, -69.25, 0.50],
    [18.71, -69.24, 0.50],
    [18.67, -69.22, 0.50],
    [18.64, -69.21, 0.50],
    [18.62, -69.21, 0.50],
    [18.59, -69.21, 0.50],
    [18.57, -69.20, 0.50],
    [18.54, -69.19, 0.50],
    [18.52, -69.19, 0.50],
    [18.50, -69.19, 0.50],
    [18.47, -69.19, 0.50],
    
    [19.09, -72.82, 0.30],
    [19.09, -72.79, 0.30],
    [19.08, -72.76, 0.30],
    [19.08, -72.72, 0.30],
    [19.09, -72.70, 0.30],
    [19.09, -72.67, 0.30],
    [19.09, -72.65, 0.30],
    [19.09, -72.61, 0.30],
    [19.10, -72.59, 0.30],
    [19.10, -72.54, 0.30],
    [19.10, -72.51, 0.30],
    [19.10, -72.48, 0.30],
    [19.10, -72.47, 0.30],
    [19.09, -72.44, 0.30],
    [19.08, -72.41, 0.30],
    [19.07, -72.39, 0.30],
    [19.05, -72.36, 0.30],
    [19.04, -72.32, 0.30],
    [19.02, -72.29, 0.30],
    [19.01, -72.28, 0.30],
    [19.00, -72.24, 0.30],
    [19.00, -72.21, 0.30],
    [18.99, -72.18, 0.30],
    [18.99, -72.14, 0.30],
    [18.99, -72.12, 0.30],
    [19.00, -72.08, 0.30],
    [19.01, -72.06, 0.30],
    [19.02, -72.02, 0.30],
    [19.02, -72.00, 0.30],
    [18.98, -71.98, 0.30],
    [18.95, -71.97, 0.30],
    [18.91, -71.97, 0.30],
    [18.87, -71.98, 0.30],
    [18.86, -72.01, 0.30],
    [18.82, -72.07, 0.30],
    [18.81, -72.13, 0.30],
    [18.79, -72.18, 0.30],
    [18.77, -72.22, 0.30],
    [18.76, -72.25, 0.30],
    [18.74, -72.28, 0.30],
    [18.74, -72.32, 0.30],
    [18.73, -72.36, 0.30],
    [18.84, -72.03, 0.30],
    [18.82, -72.10, 0.30],
    [18.80, -72.14, 0.30],
    
    [18.84, -71.23, 0.36],
    [18.85, -71.20, 0.36],
    [18.86, -71.16, 0.36],
    [18.88, -71.13, 0.36],
    [18.90, -71.09, 0.36],
    [18.90, -71.04, 0.36],
    [18.91, -71.01, 0.36],
    [18.89, -70.98, 0.36],
    [18.87, -70.96, 0.36],
    [18.86, -70.95, 0.36],
    [18.82, -70.93, 0.36],
    [18.81, -70.91, 0.36],
    [18.77, -70.89, 0.36],
    [18.75, -70.88, 0.36],
    [18.72, -70.88, 0.36],
    [18.69, -70.89, 0.36],
    [18.68, -70.92, 0.36],
    [18.67, -70.95, 0.36],
    [18.65, -70.99, 0.36],
    [18.64, -71.06, 0.36],
    [18.64, -71.03, 0.36],
    [18.64, -71.12, 0.36],
    [18.64, -71.15, 0.36],
    [18.66, -71.18, 0.36],
    [18.66, -71.21, 0.36],
    [18.69, -71.25, 0.36],
    [18.70, -71.27, 0.36],
    [18.73, -71.28, 0.36],
    [18.76, -71.27, 0.36],
    [18.80, -71.25, 0.36],
    [18.81, -71.24, 0.36],
    
    [18.67, -74.28, 0.30],
    [18.63, -74.29, 0.30],
    [18.60, -74.29, 0.30],
    [18.54, -74.28, 0.30],
    [18.51, -74.27, 0.30],
    [18.48, -74.25, 0.30],
    [18.44, -74.24, 0.30],
    [18.42, -74.22, 0.30],
    [18.38, -74.20, 0.30],
    [18.36, -74.19, 0.30],
    [18.32, -74.16, 0.30],
    [18.31, -74.15, 0.30],
    [18.30, -74.12, 0.30],
    [18.27, -74.09, 0.30],
    [18.25, -74.06, 0.30],
    [18.24, -74.04, 0.30],
    [18.22, -74.02, 0.30],
    [18.21, -73.99, 0.30],
    [18.19, -73.96, 0.30],
    [18.18, -73.94, 0.30],
    [18.17, -73.92, 0.30],
    [18.16, -73.91, 0.30],
    [18.14, -73.89, 0.30],
    [18.13, -73.88, 0.30],
    
    [18.62, -74.07, 0.34],
    [18.58, -74.09, 0.34],
    [18.54, -74.08, 0.34],
    [18.50, -74.06, 0.34],
    [18.49, -74.04, 0.34],
    [18.46, -74.02, 0.34],
    [18.44, -73.99, 0.34],
    [18.43, -73.97, 0.34],
    [18.41, -73.95, 0.34],
    [18.40, -73.91, 0.34],
    [18.40, -73.90, 0.34],
    [18.38, -73.87, 0.34],
    [18.37, -73.85, 0.34],
    [18.35, -73.81, 0.34],
    [18.34, -73.80, 0.34],
    [18.30, -73.77, 0.34],
    [18.29, -73.75, 0.34],
    [18.25, -73.73, 0.34],
    [18.25, -73.69, 0.34],
    
    [18.29, -73.41, 0.36],
    [18.30, -73.39, 0.36],
    [18.31, -73.36, 0.36],
    [18.31, -73.32, 0.36],
    [18.32, -73.30, 0.36],
    [18.32, -73.28, 0.36],
    [18.34, -73.25, 0.36],
    [18.34, -73.21, 0.36],
    [18.35, -73.19, 0.36],
    [18.35, -73.15, 0.36],
    [18.35, -73.13, 0.36],
    [18.37, -73.12, 0.36],
    [18.38, -73.08, 0.36],
    [18.38, -73.05, 0.36],
    [18.38, -73.01, 0.36],
    [18.35, -73.00, 0.36],
    [18.35, -72.97, 0.36],
    [18.32, -72.94, 0.36],
    [18.30, -72.90, 0.36],
    [18.28, -72.88, 0.36],
    [18.26, -72.86, 0.36],
    [18.25, -72.84, 0.36],
    [18.23, -72.83, 0.36],
    [18.21, -72.86, 0.36],
    [18.21, -72.86, 0.36],
    [18.19, -72.88, 0.36],
    [18.18, -72.90, 0.36],
    [18.17, -72.91, 0.36],
    
    [18.42, -70.04, 0.30],
    [18.44, -70.07, 0.30],
    [18.45, -70.11, 0.30],
    [18.48, -70.14, 0.30],
    [18.51, -70.15, 0.30],
    [18.57, -70.15, 0.30],
    [18.61, -70.15, 0.30],
    [18.62, -70.13, 0.30],
    [18.64, -70.11, 0.30],
    [18.67, -70.08, 0.30],
    [18.68, -70.06, 0.30],
    [18.70, -70.01, 0.30],
    [18.70, -69.98, 0.30],
    [18.69, -69.93, 0.30],
    [18.68, -69.89, 0.30],
    [18.68, -69.85, 0.30],
    [18.67, -69.79, 0.30],
    [18.67, -69.77, 0.30],
    [18.69, -69.73, 0.30],
    [18.69, -69.72, 0.30],
    [18.70, -69.69, 0.30],
    [18.71, -69.68, 0.30],
    [18.71, -69.66, 0.30],
    [18.72, -69.64, 0.30],
    [18.72, -69.62, 0.30],
    [18.72, -69.61, 0.30],
    [18.73, -69.57, 0.30],
    [18.74, -69.56, 0.30],
    [18.75, -69.54, 0.30],
    [18.77, -69.52, 0.30],
    [18.76, -69.49, 0.30],
    [18.75, -69.47, 0.30],
    [18.72, -69.46, 0.30],
    [18.70, -69.43, 0.30],
    [18.67, -69.42, 0.30],
    [18.65, -69.40, 0.30],
    [18.63, -69.39, 0.30],
    [18.59, -69.39, 0.30],
    [18.58, -69.39, 0.30],
    [18.54, -69.39, 0.30],
    [18.52, -69.40, 0.30],
    [18.49, -69.41, 0.30],
    [18.45, -69.43, 0.30],
    
    [18.47, -70.00, 0.25],
    [18.49, -70.02, 0.25],
    [18.51, -70.06, 0.25],
    [18.53, -70.07, 0.25],
    [18.58, -70.07, 0.25],
    [18.62, -70.05, 0.25],
    [18.64, -70.02, 0.25],
    [18.63, -69.98, 0.25],
    [18.63, -69.96, 0.25],
    [18.63, -69.93, 0.25],
    [18.61, -69.88, 0.25],
    [18.62, -69.85, 0.25],
    [18.62, -69.80, 0.25],
    [18.63, -69.77, 0.25],
    [18.63, -69.72, 0.25],
    [18.65, -69.68, 0.25],
    [18.66, -69.64, 0.25],
    [18.67, -69.59, 0.25],
    [18.67, -69.57, 0.25],
    [18.69, -69.54, 0.25],
    [18.69, -69.50, 0.25],
    [18.67, -69.47, 0.25],
    [18.63, -69.45, 0.25],
    [18.59, -69.44, 0.25],
    [18.56, -69.44, 0.25],
    [18.52, -69.44, 0.25],
    [18.48, -69.46, 0.25],
    [18.44, -69.49, 0.25],
    [17.74, -71.51, 0.42],
    [17.78, -71.49, 0.42],
    [17.83, -71.49, 0.42],
    [17.88, -71.48, 0.42],
    [17.90, -71.46, 0.42],
    [17.95, -71.45, 0.42],
    [17.97, -71.45, 0.42],
    [18.01, -71.43, 0.42],
    [18.06, -71.44, 0.42],
    [18.08, -71.44, 0.42],
    [18.14, -71.43, 0.42],
    [18.17, -71.42, 0.42],
    [18.21, -71.42, 0.42],
    [18.23, -71.39, 0.42],
    [18.27, -71.37, 0.42],
    [18.29, -71.35, 0.42],
    [18.30, -71.33, 0.42],
    [18.33, -71.30, 0.42],
    [18.35, -71.27, 0.42],
    [18.36, -71.24, 0.42],
    [18.39, -71.21, 0.42],
    [18.39, -71.18, 0.42],
    [18.41, -71.16, 0.42],
    [18.42, -71.14, 0.42],
    [18.43, -71.11, 0.42],
    [18.44, -71.09, 0.42],
    [18.44, -71.08, 0.42],
    [18.44, -71.06, 0.42],
    [18.45, -71.03, 0.42],
    [18.47, -71.02, 0.42],
    [18.47, -71.00, 0.42],
    [18.48, -70.97, 0.42],
    [18.49, -70.92, 0.42],
    [18.49, -70.89, 0.42],
    [18.49, -70.86, 0.42],
    [18.49, -70.83, 0.42],
    [18.49, -70.80, 0.42],
    [18.50, -70.75, 0.42],
    [18.49, -70.79, 0.42],
    [18.49, -70.72, 0.42],
    [18.49, -70.69, 0.42],
    [18.49, -70.65, 0.42],
    [18.48, -70.61, 0.42],
    [18.47, -70.57, 0.42],
    [18.47, -70.55, 0.42],
    [18.44, -70.48, 0.42],
    [18.44, -70.45, 0.42],
    [18.44, -70.51, 0.42],
    [18.42, -70.44, 0.42],
    [18.40, -70.41, 0.42],
    [18.40, -70.39, 0.42],
    [18.39, -70.37, 0.42],
    [18.39, -70.34, 0.42],
    [18.38, -70.33, 0.42],
    [18.36, -70.31, 0.42],
    [18.35, -70.29, 0.42],
    [18.34, -70.25, 0.42],
    [18.33, -70.22, 0.42],
    [18.31, -70.22, 0.42],
    [18.31, -70.19, 0.42],
    [18.30, -70.16, 0.42],
    [18.30, -70.15, 0.42],
    [18.30, -70.14, 0.42],
    
    [17.79, -71.34, 0.46],
    [17.83, -71.33, 0.46],
    [17.87, -71.31, 0.46],
    [17.93, -71.31, 0.46],
    [17.95, -71.30, 0.46],
    [17.97, -71.28, 0.46],
    [18.01, -71.27, 0.46],
    [18.04, -71.25, 0.46],
    [18.08, -71.23, 0.46],
    [18.11, -71.20, 0.46],
    [18.13, -71.19, 0.46],
    [18.16, -71.16, 0.46],
    [18.20, -71.14, 0.46],
    [18.21, -71.12, 0.46],
    [18.31, -71.00, 0.46],
    [18.34, -70.98, 0.46],
    [18.35, -70.94, 0.46],
    [18.35, -70.90, 0.46],
    [18.36, -70.87, 0.46],
    [18.37, -70.58, 0.46],
    [18.37, -70.54, 0.46],
    [18.35, -70.49, 0.46],
    [18.35, -70.46, 0.46],
    [18.33, -70.44, 0.46],
    [18.32, -70.40, 0.46],
    [18.30, -70.37, 0.46],
    [18.30, -70.34, 0.46],
    [18.29, -70.32, 0.46],
    [18.28, -70.29, 0.46],
    [18.26, -70.26, 0.46],
    [18.25, -70.22, 0.46],
    
    [18.57, -69.63, 0.22],
    [18.54, -69.63, 0.22],
    [18.53, -69.61, 0.22],
    [18.52, -69.58, 0.22],
    [18.51, -69.56, 0.22],
    [18.51, -69.54, 0.22],
    [18.51, -69.50, 0.22],
    [18.54, -69.50, 0.22],
    [18.58, -69.49, 0.22],
    [18.60, -69.50, 0.22],
    [18.61, -69.57, 0.22],
    [18.61, -69.53, 0.22],
    [18.61, -69.59, 0.22],
    [18.58, -69.64, 0.22],
    
    [18.24, -70.36, 0.50],
    [18.24, -70.40, 0.50],
    [18.25, -70.44, 0.50],
    [18.25, -70.50, 0.50],
    [18.26, -70.52, 0.50],
    [18.26, -70.56, 0.50],
    [18.25, -70.48, 0.50],

];
  
  